// components/RepliesPage/dialogs/ReplyRulesModal.tsx
import { useState, useEffect } from "react";
import {
  FilterX,
  Users,
  Clock,
  Shield,
  Calendar,
  Ban,
  AlertCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

// Define the reply rules type
interface ReplyRule {
  enabled: boolean;
  value: number;
}

interface ReplyRules {
  enable_moderation: boolean;
  min_followers: ReplyRule;
  max_followers: ReplyRule;
  min_account_age_days: ReplyRule;
  max_replies_per_hour: ReplyRule;
  max_replies_per_day: ReplyRule;
  custom_rules: string;
}

interface ReplyRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  replyRules: ReplyRules;
  onSave: (rules: ReplyRules) => Promise<void>;
}

const ReplyRulesModal: React.FC<ReplyRulesModalProps> = ({
  isOpen,
  onClose,
  replyRules,
  onSave,
}) => {
  // Local state for editing rules
  const [rules, setRules] = useState<ReplyRules>(replyRules);

  const [validationErrors, setValidationErrors] = useState<{
    minMaxFollowers?: string;
  }>({});

  const [inputValues, setInputValues] = useState({
    min_followers: rules.min_followers.value.toString(),
    max_followers: rules.max_followers.value.toString(),
    min_account_age_days: rules.min_account_age_days.value.toString(),
    max_replies_per_hour: rules.max_replies_per_hour.value.toString(),
    max_replies_per_day: rules.max_replies_per_day.value.toString(),
  });

  // Update local state when props change
  useEffect(() => {
    setRules(replyRules);
    setInputValues({
      min_followers: replyRules.min_followers.value.toString(),
      max_followers: replyRules.max_followers.value.toString(),
      min_account_age_days: replyRules.min_account_age_days.value.toString(),
      max_replies_per_hour: replyRules.max_replies_per_hour.value.toString(),
      max_replies_per_day: replyRules.max_replies_per_day.value.toString(),
    });
  }, [replyRules]);

  // Validate min/max follower values
  useEffect(() => {
    const minFollowers = parseInt(inputValues.min_followers) || 0;
    const maxFollowers = parseInt(inputValues.max_followers) || 0;

    if (rules.min_followers.enabled && rules.max_followers.enabled) {
      if (minFollowers > maxFollowers && maxFollowers > 0) {
        setValidationErrors((prev) => ({
          ...prev,
          minMaxFollowers:
            "Minimum followers cannot be greater than maximum followers",
        }));
      } else {
        setValidationErrors((prev) => ({
          ...prev,
          minMaxFollowers: undefined,
        }));
      }
    } else {
      setValidationErrors((prev) => ({
        ...prev,
        minMaxFollowers: undefined,
      }));
    }
  }, [
    inputValues.min_followers,
    inputValues.max_followers,
    rules.min_followers.enabled,
    rules.max_followers.enabled,
  ]);

  // Handler for saving rules
  const handleSave = () => {
    // Convert all string inputs to their final number values
    const updatedRules = {
      ...rules,
      min_followers: {
        ...rules.min_followers,
        value: parseInt(inputValues.min_followers) || 0,
      },
      max_followers: {
        ...rules.max_followers,
        value: parseInt(inputValues.max_followers) || 0,
      },
      min_account_age_days: {
        ...rules.min_account_age_days,
        value: parseInt(inputValues.min_account_age_days) || 0,
      },
      max_replies_per_hour: {
        ...rules.max_replies_per_hour,
        value: parseInt(inputValues.max_replies_per_hour) || 1,
      },
      max_replies_per_day: {
        ...rules.max_replies_per_day,
        value: parseInt(inputValues.max_replies_per_day) || 1,
      },
    };

    if (
      updatedRules.min_followers.enabled &&
      updatedRules.max_followers.enabled &&
      updatedRules.min_followers.value > updatedRules.max_followers.value &&
      updatedRules.max_followers.value > 0
    ) {
      setValidationErrors((prev) => ({
        ...prev,
        minMaxFollowers:
          "Minimum followers cannot be greater than maximum followers",
      }));
      return;
    }

    setRules(updatedRules);
    onSave(updatedRules);
  };

  const handleInputChange = (
    field: keyof typeof inputValues,
    value: string,
  ) => {
    setInputValues({
      ...inputValues,
      [field]: value,
    });

    const numValue = value === "" ? 0 : parseInt(value);
    const section = field as keyof ReplyRules;

    if (section in rules && typeof rules[section] === "object") {
      setRules((prev) => ({
        ...prev,
        [section]: {
          ...(prev[section] as ReplyRule),
          value: numValue,
        },
      }));
    }
  };

  // Update rule helper
  const updateRule = (
    section: keyof ReplyRules,
    field: keyof ReplyRule | null,
    value: boolean | number | string,
  ) => {
    if (field === null) {
      // Handle top-level fields like enable_moderation and custom_rules
      setRules((prev) => ({
        ...prev,
        [section]: value,
      }));
    } else {
      // Handle nested objects like min_followers.enabled
      setRules((prev) => {
        const sectionObj = prev[section];
        if (typeof sectionObj === "object" && sectionObj !== null) {
          return {
            ...prev,
            [section]: {
              ...(sectionObj as ReplyRule),
              [field]: value,
            },
          };
        }
        return prev;
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilterX className="h-5 w-5" />
            Enable & Manage Reply Moderation Rules
          </DialogTitle>
          <DialogDescription>
            Configure when your AI agent should reply to tweets
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-2 max-h-[70vh] overflow-y-auto pr-2">
          {/* Main moderation toggle with explanation */}
          <div className="p-4 border rounded-md bg-muted/20">
            <div className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-8">
              <div className="flex-grow">
                <div className="flex items-center gap-2">
                  <Shield className="w-5 h-5 text-blue-500" />
                  <Label
                    htmlFor="enable-moderation"
                    className="text-base font-medium"
                  >
                    Enable Reply Moderation
                  </Label>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  When enabled, replies will be filtered based on the rules
                  below
                </p>
              </div>
              <div className="flex-none flex items-center">
                <Switch
                  id="enable-moderation"
                  checked={rules.enable_moderation}
                  className="color-toggle"
                  onCheckedChange={(checked) =>
                    updateRule("enable_moderation", null, checked)
                  }
                />
              </div>
            </div>

            {!rules.enable_moderation && (
              <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-md flex items-start">
                <Ban className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0 mr-2" />
                <p className="text-sm text-amber-700 dark:text-amber-400">
                  Moderation is currently disabled. Your agent will attempt to
                  respond to all mentions without filtering. Enable moderation
                  to control which tweets receive a response.
                </p>
              </div>
            )}
          </div>

          {/* Validation error message for min/max followers */}
          {validationErrors.minMaxFollowers && (
            <div className="p-3 bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-800 rounded-md flex items-start">
              <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0 mr-2" />
              <p className="text-sm text-red-700 dark:text-red-400">
                {validationErrors.minMaxFollowers}
              </p>
            </div>
          )}

          {/* Rule Sections */}
          <div className="grid gap-4 grid-cols-1 md:grid-cols-2">
            {/* Min followers */}
            <div className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <Label
                    htmlFor="min-followers-enabled"
                    className="font-medium"
                  >
                    Minimum Followers
                  </Label>
                </div>
                <Switch
                  className="color-toggle"
                  id="min-followers-enabled"
                  checked={rules.min_followers.enabled}
                  onCheckedChange={(checked) =>
                    updateRule("min_followers", "enabled", checked)
                  }
                  disabled={!rules.enable_moderation}
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={inputValues.min_followers}
                  onChange={(e) =>
                    handleInputChange("min_followers", e.target.value)
                  }
                  disabled={
                    !rules.min_followers.enabled || !rules.enable_moderation
                  }
                  className="max-w-[120px]"
                />
                <p className="text-sm text-muted-foreground">
                  Only reply to users with at least this many followers
                </p>
              </div>
            </div>

            {/* Max followers */}
            <div className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-blue-500" />
                  <Label
                    htmlFor="max-followers-enabled"
                    className="font-medium"
                  >
                    Maximum Followers
                  </Label>
                </div>
                <Switch
                  className="color-toggle"
                  id="max-followers-enabled"
                  checked={rules.max_followers.enabled}
                  onCheckedChange={(checked) =>
                    updateRule("max_followers", "enabled", checked)
                  }
                  disabled={!rules.enable_moderation}
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={inputValues.max_followers}
                  onChange={(e) =>
                    handleInputChange("max_followers", e.target.value)
                  }
                  disabled={
                    !rules.max_followers.enabled || !rules.enable_moderation
                  }
                  className="max-w-[120px]"
                />
                <p className="text-sm text-muted-foreground">
                  Only reply to users with at most this many followers
                </p>
              </div>
            </div>

            {/* Min account age */}
            <div className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-blue-500" />
                  <Label
                    htmlFor="min-account-age-enabled"
                    className="font-medium"
                  >
                    Minimum Account Age (days)
                  </Label>
                </div>
                <Switch
                  className="color-toggle"
                  id="min-account-age-enabled"
                  checked={rules.min_account_age_days.enabled}
                  onCheckedChange={(checked) =>
                    updateRule("min_account_age_days", "enabled", checked)
                  }
                  disabled={!rules.enable_moderation}
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="0"
                  value={inputValues.min_account_age_days}
                  onChange={(e) =>
                    handleInputChange("min_account_age_days", e.target.value)
                  }
                  disabled={
                    !rules.min_account_age_days.enabled ||
                    !rules.enable_moderation
                  }
                  className="max-w-[120px]"
                />
                <p className="text-sm text-muted-foreground">
                  Only reply to accounts that are at least this many days old
                </p>
              </div>
            </div>

            {/* Max replies per hour */}
            <div className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <Label
                    htmlFor="max-replies-hour-enabled"
                    className="font-medium"
                  >
                    Maximum Replies Per Hour
                  </Label>
                </div>
                <Switch
                  className="color-toggle"
                  id="max-replies-hour-enabled"
                  checked={rules.max_replies_per_hour.enabled}
                  onCheckedChange={(checked) =>
                    updateRule("max_replies_per_hour", "enabled", checked)
                  }
                  disabled={!rules.enable_moderation}
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={inputValues.max_replies_per_hour}
                  onChange={(e) =>
                    handleInputChange("max_replies_per_hour", e.target.value)
                  }
                  disabled={
                    !rules.max_replies_per_hour.enabled ||
                    !rules.enable_moderation
                  }
                  className="max-w-[120px]"
                />
                <p className="text-sm text-muted-foreground">
                  Limit the number of replies your agent can post per hour
                </p>
              </div>
            </div>

            {/* Max replies per day */}
            <div className="p-4 border rounded-md">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <Label
                    htmlFor="max-replies-day-enabled"
                    className="font-medium"
                  >
                    Maximum Replies Per Day
                  </Label>
                </div>
                <Switch
                  className="color-toggle"
                  id="max-replies-day-enabled"
                  checked={rules.max_replies_per_day.enabled}
                  onCheckedChange={(checked) =>
                    updateRule("max_replies_per_day", "enabled", checked)
                  }
                  disabled={!rules.enable_moderation}
                />
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="number"
                  min="1"
                  value={inputValues.max_replies_per_day}
                  onChange={(e) =>
                    handleInputChange("max_replies_per_day", e.target.value)
                  }
                  disabled={
                    !rules.max_replies_per_day.enabled ||
                    !rules.enable_moderation
                  }
                  className="max-w-[120px]"
                />
                <p className="text-sm text-muted-foreground">
                  Limit the number of replies your agent can post per day
                </p>
              </div>
            </div>
          </div>

          {/* Custom rules text field */}
          <div className="p-4 border rounded-md">
            <Label
              htmlFor="custom-rules"
              className="font-medium block mb-2 flex items-center gap-2"
            >
              <Shield className="w-4 h-4 text-blue-500" />
              Custom Reply Rules
            </Label>
            <Textarea
              id="custom-rules"
              className="w-full min-h-[120px]"
              placeholder="Add any specific instructions for when your agent should or shouldn't reply..."
              value={rules.custom_rules}
              onChange={(e) => updateRule("custom_rules", null, e.target.value)}
              disabled={!rules.enable_moderation}
            />
            <p className="text-sm text-muted-foreground mt-2">
              Add natural language instructions about when to reply. Examples:
            </p>
            <ul className="list-disc pl-5 mt-1 text-sm text-muted-foreground">
              <li>Don't reply to tweets containing profanity</li>
              <li>Only reply to tweets asking questions</li>
              <li>Prioritize replies to tweets mentioning specific keywords</li>
              <li>
                Don't reply to users who have been responded to in the last 24
                hours
              </li>
            </ul>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={!!validationErrors.minMaxFollowers}
          >
            Save Rules
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ReplyRulesModal;
